import React, { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"
import {
  Inventory as InventoryIcon,
  LocationOn as LocationIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts"
import { apiService } from "../services/apiService"

interface InventoryData {
  locations: Array<{
    id: string
    name: string
    type: string
    address: string
    capacity: number
    utilization: number
    totalSkus: number
    totalValue: number
  }>
  summary: {
    totalLocations: number
    totalSkus: number
    totalValue: number
    averageUtilization: number
    lowStockAlerts: number
    movementsToday: number
  }
  topSkus: Array<{
    sku: string
    totalQuantity: number
    totalValue: number
    locations: number
  }>
}

const Inventory: React.FC = () => {
  const [inventoryData, setInventoryData] = useState<InventoryData | null>(null)
  const [loading, setLoading] = useState(true)
  const [skuDialogOpen, setSkuDialogOpen] = useState(false)
  const [skuToCheck, setSkuToCheck] = useState("")
  const [skuAvailability, setSkuAvailability] = useState<any>(null)

  useEffect(() => {
    fetchInventoryData()
  }, [])

  const fetchInventoryData = async () => {
    try {
      setLoading(true)
      const data = await apiService.getInventory()
      setInventoryData(data)
    } catch (error) {
      console.error("Failed to fetch inventory data:", error)
    } finally {
      setLoading(false)
    }
  }

  const checkSkuAvailability = async () => {
    if (!skuToCheck) return

    try {
      const data = await apiService.checkSkuAvailability(skuToCheck)
      setSkuAvailability(data)
      setSkuDialogOpen(true)
    } catch (error) {
      console.error("Failed to check SKU availability:", error)
    }
  }

  if (loading) {
    return <LinearProgress />
  }

  if (!inventoryData) {
    return <Alert severity="error">Failed to load inventory data</Alert>
  }

  const utilizationData = inventoryData.locations.map((loc) => ({
    name: loc.name.split(" ")[0], // First word of location name
    utilization: Math.round(loc.utilization * 100),
    capacity: loc.capacity,
    value: loc.totalValue / 1000000, // Convert to millions
  }))

  const skuValueData = inventoryData.topSkus.map((sku) => ({
    name: sku.sku,
    value: sku.totalValue / 1000, // Convert to thousands
    quantity: sku.totalQuantity,
  }))

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            background: "linear-gradient(135deg, #1a237e 0%, #534bae 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 1,
          }}
        >
          Inventory Management
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ fontWeight: 400 }}
        >
          Real-time inventory tracking and management
        </Typography>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Locations
                  </Typography>
                  <Typography variant="h4">
                    {inventoryData.summary.totalLocations}
                  </Typography>
                </Box>
                <LocationIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total SKUs
                  </Typography>
                  <Typography variant="h4">
                    {inventoryData.summary.totalSkus.toLocaleString()}
                  </Typography>
                </Box>
                <InventoryIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Value
                  </Typography>
                  <Typography variant="h4">
                    ${(inventoryData.summary.totalValue / 1000000).toFixed(1)}M
                  </Typography>
                </Box>
                <TrendingUpIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Low Stock Alerts
                  </Typography>
                  <Typography
                    variant="h4"
                    color={
                      inventoryData.summary.lowStockAlerts > 0
                        ? "error"
                        : "success"
                    }
                  >
                    {inventoryData.summary.lowStockAlerts}
                  </Typography>
                </Box>
                <WarningIcon
                  color={
                    inventoryData.summary.lowStockAlerts > 0
                      ? "error"
                      : "success"
                  }
                  sx={{ fontSize: 40 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* SKU Checker */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            SKU Availability Checker
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              label="Enter SKU"
              value={skuToCheck}
              onChange={(e) => setSkuToCheck(e.target.value)}
              placeholder="e.g., HEAVY-MACH-001"
            />
            <Button
              variant="contained"
              onClick={checkSkuAvailability}
              disabled={!skuToCheck}
            >
              Check Availability
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Warehouse Utilization
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={utilizationData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value, name) => [`${value}%`, name]} />
                  <Bar dataKey="utilization" fill="#1976d2" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top SKUs by Value
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={skuValueData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: $${value}K`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {skuValueData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={`hsl(${index * 60}, 70%, 50%)`}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`$${value}K`, "Value"]} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Locations List */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Warehouse Locations
              </Typography>
              <List>
                {inventoryData.locations.map((location, index) => (
                  <React.Fragment key={location.id}>
                    <ListItem>
                      <ListItemIcon>
                        <LocationIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={location.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {location.address}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                              <Chip
                                label={`${Math.round(location.utilization * 100)}% utilized`}
                                color={
                                  location.utilization > 0.8
                                    ? "error"
                                    : location.utilization > 0.6
                                      ? "warning"
                                      : "success"
                                }
                                size="small"
                              />
                              <Chip
                                label={`${location.totalSkus} SKUs`}
                                variant="outlined"
                                size="small"
                              />
                              <Chip
                                label={`$${(location.totalValue / 1000000).toFixed(1)}M`}
                                variant="outlined"
                                size="small"
                              />
                            </Box>
                            <Box mt={1}>
                              <LinearProgress
                                variant="determinate"
                                value={location.utilization * 100}
                                color={
                                  location.utilization > 0.8
                                    ? "error"
                                    : location.utilization > 0.6
                                      ? "warning"
                                      : "success"
                                }
                              />
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < inventoryData.locations.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Activity
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Inbound Movement"
                    secondary="HEAVY-MACH-001 • 5 units • 2 hours ago"
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <TrendingUpIcon color="info" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Transfer Complete"
                    secondary="SPARE-PARTS-042 • 50 units • 1 hour ago"
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemIcon>
                    <WarningIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Low Stock Alert"
                    secondary="ELECTRONICS-X1 • 10 units remaining"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* SKU Availability Dialog */}
      <Dialog
        open={skuDialogOpen}
        onClose={() => setSkuDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>SKU Availability: {skuToCheck}</DialogTitle>
        <DialogContent>
          {skuAvailability && (
            <Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Available
                  </Typography>
                  <Typography variant="h6">
                    {skuAvailability.summary.totalAvailable}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Can Fulfill
                  </Typography>
                  <Chip
                    label={
                      skuAvailability.summary.canFulfillAcrossLocations
                        ? "Yes"
                        : "No"
                    }
                    color={
                      skuAvailability.summary.canFulfillAcrossLocations
                        ? "success"
                        : "error"
                    }
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" gutterBottom>
                    Locations with Stock
                  </Typography>
                  <List>
                    {skuAvailability.locations.map(
                      (loc: any, index: number) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={loc.locationName}
                            secondary={
                              <Box>
                                <Typography variant="body2">
                                  Available: {loc.availableQuantity} | Reserved:{" "}
                                  {loc.reservedQuantity}
                                </Typography>
                                <Chip
                                  label={
                                    loc.canFulfill
                                      ? "Can Fulfill"
                                      : "Cannot Fulfill"
                                  }
                                  color={loc.canFulfill ? "success" : "error"}
                                  size="small"
                                />
                              </Box>
                            }
                          />
                        </ListItem>
                      )
                    )}
                  </List>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkuDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Inventory
